import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RxCollection, RxDocument } from 'rxdb';

import { SearchStatusEnum } from '@mopla/data-models';
import { safeRemoveDocument } from '@mopla/utils';

import { type BusinessLayer, useBusinessLayer } from '../business-logic';
import type { ICapacityCheckDocType } from '../schemas/capacityCheck';
import { LBODTFLEXApi } from '../services/capacityCheck/api/lbodtflexAPI';
import { PRODTApi } from '../services/capacityCheck/api/prodtAPI';

type AnyCCDoc = RxDocument<ICapacityCheckDocType<'ANY'>>;

/**
 * When a user searches for a PRODT/LBODTFLEX ride, the ride is initially reserved (prebooked).
 * This hook ensures stale CC docs in RxDB are reconciled on app start so the driver
 * does not stay frozen for an abandoned reservation.
 *
 * Each stale doc → GET on BE. Locally remove only when BE reports CANCELLED / EXPIRED
 * (or returns an error — the resource is gone). IN_PROGRESS / SUCCESS docs are left
 * untouched, so a second tab cannot kill the first tab's reservation.
 *
 * Skipped when `isBankRedirect` query param is present — the user is returning from a 3rd-party
 * payment page and the active reservation must be preserved.
 */
export const usePreBookingCleanup = () => {
	const bl = useBusinessLayer();
	const { db } = bl;
	const [query] = useSearchParams();
	const isBankRedirect = query.has('isBankRedirect');

	useEffect(() => {
		if (isBankRedirect) return;

		(async () => {
			const capacityCheckCol = db.collections['capacityCheck'] as RxCollection<
				ICapacityCheckDocType<'ANY'>
			>;
			const capacityCheckDocs = await capacityCheckCol.find().exec();

			for (const doc of capacityCheckDocs) {
				await reconcileWithBackend(doc, bl);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};

async function reconcileWithBackend(
	doc: AnyCCDoc,
	bl: BusinessLayer
): Promise<void> {
	if (!doc.capacityCheckId) {
		await safeRemoveDocument<ICapacityCheckDocType<'ANY'>>(doc);
		return;
	}

	const api =
		doc.mode === 'PRODT'
			? new PRODTApi(bl.api, bl.db)
			: new LBODTFLEXApi(bl.api, bl.db);

	try {
		const remoteState = await api.getSearch(doc.capacityCheckId);

		if (
			remoteState.status === SearchStatusEnum.CANCELLED ||
			remoteState.status === SearchStatusEnum.EXPIRED
		) {
			await safeRemoveDocument<ICapacityCheckDocType<'ANY'>>(doc);
		}
		/** IN_PROGRESS / SUCCESS / NOTHING_FOUND → leave doc untouched. */
	} catch {
		/**
		 * Resource is not on the BE anymore (404) or the call failed —
		 * dropping the local doc is the safe choice; a stale doc would
		 * never recover otherwise.
		 */
		await safeRemoveDocument<ICapacityCheckDocType<'ANY'>>(doc);
	}
}
